/**
 * Oversight service for the ms-agents backend (Flex Comms module).
 *
 * Each function takes the ms-agents verb (imported from `commsApi.js`) as its
 * first argument. Return-shape note: `get` returns `response.data`; `post`
 * returns `{ data, status }`.
 */
import { buildQuery } from "./_query";

/**
 * List the clients assigned to a coach.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} coachId - Coach id.
 * @returns {Promise<Object>} Coach clients payload.
 */
export const getCoachClients = async (get, coachId) => {
  try {
    return await get(`/api/v1/oversight/coaches/${coachId}/clients`);
  } catch (err) {
    const message = err?.response?.data?.message || "Error fetching coach clients";
    console.error(message);
    throw err;
  }
};

/**
 * Mute or set AI mode company-wide.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {Object} payload - `{ company_id, ai_mode }`.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const aiMute = async (post, payload) => {
  try {
    return await post(`/api/v1/oversight/ai-mute`, payload);
  } catch (err) {
    const message = err?.response?.data?.message || "Error updating AI mute";
    console.error(message);
    throw err;
  }
};

/**
 * List crisis flags.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {boolean} [unresolvedOnly=true] - Only return unresolved flags.
 * @returns {Promise<Object>} Crisis flags payload.
 */
export const getCrisisFlags = async (get, unresolvedOnly = true) => {
  try {
    return await get(`/api/v1/oversight/crisis-flags${buildQuery({ unresolved_only: unresolvedOnly })}`);
  } catch (err) {
    const message = err?.response?.data?.message || "Error fetching crisis flags";
    console.error(message);
    throw err;
  }
};
